import { useNavigator } from '@repo/toolkit/use-navigator'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { getOlHostFamilyPrice } from '../../../api/ols/host-family-prices/get-ol-host-family-price'
import { updateOlHostFamilyPrice } from '../../../api/ols/host-family-prices/update-ol-host-family-price'
import { ItemNotFound } from '../../../components/elements/item-not-found'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import OlHostFamilyPriceForm from '../../../components/pages/ols/ol-host-family-prices/ol-host-family-price-form'
import { mapOlHostFamilyPriceToInput } from '../../../components/pages/ols/utils'
import { Paths } from '../../../lib/constants/path'
import { twoButtonsConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { constructOlPath } from './utils'

type Props = {
  olId: number
}

const OlHostFamilyPriceUpdate: React.FC<Props> = ({ olId }) => {
  const { hostFamilyPriceId: id } = useParams<{ hostFamilyPriceId: string }>()
  const { back } = useNavigator()
  const queryClient = useQueryClient()

  if (!id || isNaN(+id)) return <ItemNotFound title="Identifiant de famille hôtelière invalide" />

  const { data: olHostFamilyPrice, isPending } = useQuery({
    queryKey: ['ol-host-family-price', id],
    queryFn: () => getOlHostFamilyPrice({ id: +id, olId })
  })

  const { mutateAsync } = useMutation({
    mutationFn: updateOlHostFamilyPrice,
    onError: showAxiosError,
    onSuccess() {
      queryClient.invalidateQueries({ exact: true, queryKey: ['ol-host-family-price', id] })
      toast.success('Famille hôtelière modifiée avec succès')
    }
  })

  if (isPending) return <LoadingScreen />
  if (!olHostFamilyPrice) return <ItemNotFound title="Prix de famille d'accueil non trouvé" />

  return (
    <div>
      <OlHostFamilyPriceForm
        olId={olId}
        mode="update"
        defaultValues={mapOlHostFamilyPriceToInput(olHostFamilyPrice)}
        buttonsConfig={twoButtonsConfig({
          onCancel: () => back(constructOlPath(Paths.OL_DETAILS_HOST_FAMILY_PRICES_PAGE, olId)),
          onConfirm: (values) => mutateAsync({ id: +id, olId, ...values })
        })}
      />
    </div>
  )
}

export default OlHostFamilyPriceUpdate
